import {
  aiAssistantEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useState,
} from 'react';
import {
  notEmptyString,
} from '@populateui/utils';
import useRealTime from 'views/EMR/components/AIAssistant/useRealTime';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  AgentStatus,
  ChatMessage,
  ResponseDone,
  ResponseTextDone,
} from 'views/EMR/components/AIAssistant/types';
import head from 'lodash/head';

export const commonContext = `
- You are Populate's AI assistant, 
  expert about answering Questions from Populate knowledge base using Appointment, Patient information and billing information.
  (Populate is an EMR system that manages appointments, billing and revenue management for this clinic.)
- You do not have a name. Do not mention your name.
- Please keep a warm, friendly, clear and professional tone during your conversation.
- Begin the conversation with a warm and professional greeting.
- Maintain a professional, empathetic, and polite tone throughout the conversation.
- Keep responses concise and focused.
- Do not repeat the same question.
- If your answer is a list, render it as a list. use two new lines to separate the items.
- If your answer is a table, render it as a table. use two new lines to separate the rows.
- If your answer is a paragraph, render it as a paragraph.
- Do not use bold, italic, or any other formatting in your response.
`;

export default function usePopulateAIAssistant() {
  const [ready, setReady] = useState<boolean>(false);
  const [chatMessageList, setChatMessageList] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<ErrorEvent | null>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatus>({
    userTalking: false,
    assistantTalking: false,
  });

  const addChatMessage = (type: 'user' | 'assistant', content: string, id: string) => {
    if (notEmptyString(content)) {
      setChatMessageList((prev) => [...prev, {
        id,
        type,
        content,
      }]);
    }
  };

  const onResponseDone = (data: ResponseDone) => {
    const content = head(data?.response?.output ?? [])?.content ?? [];
    const transcript = head(content)?.transcript ?? '';
    addChatMessage('assistant', transcript, data?.event_id);
  };

  const onResponseTextDone = (data: ResponseTextDone) => {
    addChatMessage('assistant', data?.text ?? '', data?.event_id ?? '');
  };

  const onUserAudioTranscriptionCompleted = (data: any) => {
    const content = data?.transcript;
    addChatMessage('user', content, data?.event_id);
    aiAssistantEventChannel.emit('onNewUserMessage', content);
  };

  const onUserStartSpeaking = () => {
    setAgentStatus((prev) => ({ ...prev, userTalking: true }));
  };

  const onUserStopSpeaking = () => {
    setAgentStatus((prev) => ({ ...prev, userTalking: false }));
  };

  const onError = (data: ErrorEvent) => {
    setError(data);
  };

  const userInfo = useUserInfo();

  const {
    startSession,
    stopSession,
    sendUserTextMessage,
    addContext,
    responseDelta,
  } = useRealTime({
    onSessionStarted: () => {
      setReady(true);
      addContext(`You are talking to ${userInfo?.user?.designation} ${userInfo?.user?.firstName} ${userInfo?.user?.lastName}. ${commonContext}`);
    },
    onReceivedResponseDone: onResponseDone,
    onReceivedResponseTextDone: onResponseTextDone,
    onReceivedInputAudioTranscriptionCompleted: onUserAudioTranscriptionCompleted,
    onReceivedInputAudioBufferSpeechStarted: onUserStartSpeaking,
    onReceivedInputAudioBufferSpeechStopped: onUserStopSpeaking,
    onReceivedError: onError,
  });

  const sendTextMessage = (message: string) => {
    const eventId = sendUserTextMessage(message);
    setChatMessageList((prev) => [...prev, { id: eventId, type: 'user', content: message }]);
  };

  return {
    ready,
    startSession,
    stopSession,
    sendTextMessage,
    addContext,
    agentStatus,
    error,
    chatMessageList,
    responseDelta,
  };
}
